import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEO from '../../../../shared/components/SEO/SEO';
import { useAuth } from '../../../../contexts/AuthContext';
import { forumAPI, type ForumComment, type ForumPost } from '../../../../services/api';
import { stripHtml } from './utils';
import { UserAvatar } from './UserAvatar';
import './Community.css';

const COMMENT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
const MAX_COMMENT_IMAGE_MB = 5;

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const commentImageInputRef = useRef<HTMLInputElement>(null);
  const [post, setPost] = useState<ForumPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentBody, setCommentBody] = useState('');
  const [commentImage, setCommentImage] = useState<File | null>(null);
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deletingPost, setDeletingPost] = useState(false);
  const isPostAuthor = !!post && !!user && post.user.id === user.id;

  const onCommentImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > MAX_COMMENT_IMAGE_MB * 1024 * 1024) return;
    setCommentImage(file);
    if (commentImageInputRef.current) commentImageInputRef.current.value = '';
  };

  const loadPost = useCallback(() => {
    if (!id) return;
    setLoading(true);
    forumAPI.getPost(Number(id))
      .then((res) => setPost(res.data as ForumPost))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => loadPost(), [loadPost]);

  const handleLike = () => {
    if (!isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/community/p/' + id));
      return;
    }
    if (!post) return;
    forumAPI.likePost(post.id)
      .then((res) => {
        setPost((p) => p ? { ...p, liked: res.data.liked, like_count: res.data.like_count } : null);
      })
      .catch(() => {});
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/community/p/' + id));
      return;
    }
    if (!post || !commentBody.trim()) return;
    setSubmitting(true);
    setError('');
    forumAPI.createComment(
      post.id,
      { body: commentBody.trim(), parent: replyTo ?? undefined },
      commentImage && commentImage.type.startsWith('image/') ? commentImage : undefined
    )
      .then(() => {
        setCommentBody('');
        setCommentImage(null);
        setReplyTo(null);
        if (commentImageInputRef.current) commentImageInputRef.current.value = '';
        loadPost();
      })
      .catch((err) => setError(err.response?.data?.detail || 'Failed to post comment'))
      .finally(() => setSubmitting(false));
  };

  const handleDeletePost = () => {
    if (!post || !window.confirm('Delete this post? This cannot be undone.')) return;
    setDeletingPost(true);
    forumAPI.deletePost(post.id)
      .then(() => navigate('/community', { replace: true }))
      .catch(() => setDeletingPost(false));
  };

  if (loading || !id) {
    return (
      <div className="community-page">
        <div className="community-empty">Loading...</div>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="community-page">
        <div className="community-empty">Post not found.</div>
        <Link to="/community" className="community-btn secondary" style={{ marginTop: '1rem' }}>Back to Community</Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={`${post.title} - Crypxa Community`} description={post.body.slice(0, 160)} />
      <div className="community-page">
        <div className="community-header">
          <Link to="/community" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', textDecoration: 'none', marginBottom: '0.5rem' }}>
            ← Community
          </Link>
          {isAuthenticated ? (
            <Link to="/community/new" className="community-btn">+ New post</Link>
          ) : (
            <Link to={'/login?next=' + encodeURIComponent('/community/p/' + id)} className="community-btn">Log in to post</Link>
          )}
        </div>
        <div className="community-post-detail">
          <div className="meta">
            {post.is_pinned && <span className="pinned">Pinned</span>}
            {post.status !== 'published' && <span className={`status-badge status-${post.status}`} style={{ padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600 }}>{post.status}</span>}
            <Link to={`/community/c/${post.category.slug}`} style={{ color: 'var(--secondary-color)', fontWeight: 600, textDecoration: 'none' }}>
              {post.category.name}
            </Link>
            <UserAvatar user={post.user} size={32} />
            <span>{post.user.display_name}</span>
            <span>{new Date(post.created_at).toLocaleString()}</span>
            <span>{post.views} views</span>
          </div>
          <h1>{post.title}</h1>
          {isPostAuthor && post.status === 'rejected' && (
            <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: 'rgba(185, 28, 28, 0.08)', border: '1px solid rgba(185, 28, 28, 0.3)', borderRadius: 8, color: 'var(--color-Error, #b91c1c)', fontSize: '0.9rem' }}>
              This post was rejected. You cannot edit it. You can create a new post if you’d like to share again.
            </div>
          )}
          <div className="body community-body-html" dangerouslySetInnerHTML={{ __html: post.body || '' }} />
          {post.attachments && post.attachments.length > 0 && (
            <div className="community-post-images">
              {post.attachments.map((att) => (
                <a key={att.id} href={att.file_url} target="_blank" rel="noopener noreferrer" className="community-post-image-link">
                  <img src={att.file_url} alt="" className="community-post-image" />
                </a>
              ))}
            </div>
          )}
          <div className="actions community-detail-actions">
            <button type="button" className={`like-btn ${post.liked ? 'liked' : ''}`} onClick={handleLike} aria-label="Like">
              {(post.like_count ?? 0) > 0 || post.liked ? <i className={`fas fa-heart${post.liked ? '' : '-o'}`} aria-hidden></i> : null} {post.like_count ?? 0} likes
            </button>
            <span className="community-detail-stat"><i className="fas fa-comment" aria-hidden></i> {post.comments?.length ?? 0} comments</span>
            <button
              type="button"
              className="community-btn community-btn-ghost share-btn"
              onClick={() => {
                const url = window.location.href;
                if (navigator.share) {
                  navigator.share({ title: post.title, url }).catch(() => { navigator.clipboard?.writeText(url); });
                } else {
                  navigator.clipboard?.writeText(url);
                }
              }}
              aria-label="Share"
            >
              <i className="fas fa-share-alt" aria-hidden></i> Share
            </button>
            <span className="community-detail-stat"><i className="fas fa-eye" aria-hidden></i> {post.views} views</span>
            {isPostAuthor && post.status === 'pending' && (
              <Link to={`/community/edit/${post.id}`} className="community-btn secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.9rem', marginLeft: 'auto' }}><i className="fas fa-edit" style={{ marginRight: '0.35rem' }} aria-hidden></i>Edit post</Link>
            )}
          </div>
        </div>
        <div className="community-comments-section">
          <h3>Comments ({post.comments?.length ?? 0})</h3>
          {post.status !== 'published' && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>Comments are only available once the post is approved and published.</p>
          )}
          {post.status === 'published' && !post.is_locked && (
            <div className="community-comment-form">
              {!isAuthenticated ? (
                <div className="login-prompt">
                  <Link to={'/login?next=' + encodeURIComponent('/community/p/' + id)} className="community-btn">Log in</Link>
                  {' '}to comment or like.
                </div>
              ) : (
                <form className="community-comment-form-inner" onSubmit={handleSubmitComment}>
                  {replyTo && (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                      Replying. <button type="button" onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', color: 'var(--secondary-color)', cursor: 'pointer', textDecoration: 'underline' }}>Cancel</button>
                    </p>
                  )}
                  <textarea value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Write a comment..." required minLength={1} />
                  <div className="community-comment-image-row">
                    <input
                      ref={commentImageInputRef}
                      type="file"
                      accept={COMMENT_IMAGE_ACCEPT}
                      onChange={onCommentImageChange}
                      className="community-file-input-hidden"
                      aria-label="Add image"
                    />
                    {!commentImage ? (
                      <button
                        type="button"
                        className="community-btn community-btn-ghost"
                        onClick={() => commentImageInputRef.current?.click()}
                      >
                        Add image (1 max, {MAX_COMMENT_IMAGE_MB}MB)
                      </button>
                    ) : (
                      <>
                        <div className="community-comment-image-preview-wrap">
                          <img src={URL.createObjectURL(commentImage)} alt="" className="community-comment-image-preview" />
                          <button type="button" className="community-comment-image-clear" onClick={() => { setCommentImage(null); commentImageInputRef.current && (commentImageInputRef.current.value = ''); }}>Remove image</button>
                        </div>
                      </>
                    )}
                  </div>
                  {error && <p style={{ color: 'var(--color-Error)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{error}</p>}
                  <button type="submit" className="community-btn" disabled={submitting || !commentBody.trim()}>
                    {submitting ? 'Posting...' : 'Post comment'}
                  </button>
                </form>
              )}
            </div>
          )}
          {post.is_locked && <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>This post is locked.</p>}
          {post.comments && post.comments.length > 0 ? (
            post.comments.map((comment) => (
              <CommentBlock
                key={comment.id}
                comment={comment}
                isAuthenticated={isAuthenticated}
                currentUserId={user?.id}
                onReply={() => setReplyTo(comment.id)}
                onLoginRedirect={() => navigate('/login?next=' + encodeURIComponent('/community/p/' + id))}
                onCommentUpdated={loadPost}
              />
            ))
          ) : (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>No comments yet.</p>
          )}
        </div>
      </div>
    </>
  );
};

function CommentBlock({
  comment,
  isAuthenticated,
  currentUserId,
  onReply,
  onLoginRedirect,
  onCommentUpdated,
}: {
  comment: ForumComment;
  isAuthenticated: boolean;
  currentUserId?: number;
  onReply: () => void;
  onLoginRedirect: () => void;
  onCommentUpdated: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isAuthor = currentUserId !== undefined && comment.user.id === currentUserId;

  const handleSaveEdit = () => {
    if (editBody.trim() === comment.body.trim()) {
      setEditing(false);
      return;
    }
    setSubmitting(true);
    forumAPI.updateComment(comment.id, editBody.trim())
      .then(() => {
        setEditing(false);
        onCommentUpdated();
      })
      .finally(() => setSubmitting(false));
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this comment?')) return;
    setDeleting(true);
    forumAPI.deleteComment(comment.id)
      .then(() => onCommentUpdated())
      .finally(() => setDeleting(false));
  };

  return (
    <div className={`community-comment ${comment.parent ? 'reply' : ''}`}>
      <div className="comment-meta">
        <UserAvatar user={comment.user} size={28} />
        <span>{comment.user.display_name}</span>
        {comment.user.is_staff && <span className="community-badge-admin">Admin</span>}
        <span> · {new Date(comment.created_at).toLocaleString()}</span>
        {!comment.parent && (
          <button type="button" onClick={isAuthenticated ? onReply : onLoginRedirect} style={{ marginLeft: '0.75rem', background: 'none', border: 'none', color: 'var(--secondary-color)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>Reply</button>
        )}
        {isAuthor && !editing && (
          <>
            <button type="button" onClick={() => setEditing(true)} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>Edit</button>
            <button type="button" onClick={handleDelete} disabled={deleting} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--color-Error)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>{deleting ? 'Deleting...' : 'Delete'}</button>
          </>
        )}
      </div>
      {editing ? (
        <div style={{ marginTop: '0.5rem' }}>
          <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={3} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '0.95rem' }} />
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="community-btn" onClick={handleSaveEdit} disabled={submitting || !editBody.trim()}>{submitting ? 'Saving...' : 'Save'}</button>
            <button type="button" className="community-btn secondary" onClick={() => { setEditing(false); setEditBody(comment.body); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="comment-body">{comment.body}</div>
          {comment.attachments && comment.attachments.length > 0 && (
            <div className="community-comment-images">
              {comment.attachments.map((att) => (
                <a key={att.id} href={att.file_url} target="_blank" rel="noopener noreferrer" className="community-comment-image-link">
                  <img src={att.file_url} alt="" className="community-comment-image" />
                </a>
              ))}
            </div>
          )}
        </>
      )}
      {comment.replies && comment.replies.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          {comment.replies.map((r) => (
            <CommentBlock key={r.id} comment={r} isAuthenticated={isAuthenticated} currentUserId={currentUserId} onReply={onReply} onLoginRedirect={onLoginRedirect} onCommentUpdated={onCommentUpdated} />
          ))}
        </div>
      )}
    </div>
  );
}

export default PostDetail;
